import { buildNextPurchasePeriod } from "@taabl/common-lib/calculators/period";
import { round } from "@taabl/common-lib/utils";
import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Form,
  Modal,
  OverlayTrigger,
  Tab,
  Tabs,
  Tooltip
} from "react-bootstrap";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory, { PaginationProvider } from "react-bootstrap-table2-paginator";
import ToolkitProvider from "react-bootstrap-table2-toolkit";
import { confirmAlert } from "react-confirm-alert";
import { FaRegClock } from "react-icons/fa";
import { toast } from "react-toastify";
import DelayedInput from "../components/DelayedInput";
import Icons from "../components/Icons";
import PageLoader from "../components/PageLoader";
import ViewFileURL from "../components/ViewFileURL";
import "../containers/objectList.scss";
import { Constants } from "../libs/Constants";
import { create, delObj, getList, update } from "../libs/dbFunctions";
import { convertDate, formatCurrency, formatDate } from "../libs/utils";
import { getConfig } from "../Objects/Object";
import { getUserConfig } from "../Objects/UserObj";
import Contributions from "./Contributions";
import EmployeeHome from "./EmployeeHome";
import "./Home.css";
import SideSheet from "./SideSheet";

export default function List(props) {
  const [displayObjects, setDisplayObjects] = useState([]);
  //only used for the initial load.
  const [type, setType] = useState(null);
  const [config, setConfig] = useState(null);
  const [isESPP, setIsESPP] = useState(false);
  const [periods, setPeriods] = useState(false);
  const [plan, setPlan] = useState({});
  const [openSideSheet, setOpenSideSheet] = useState(null);
  const [tab, setTab] = useState(null);
  const permissionValue = getUserConfig()?.fieldTypes?.permission?.options;
  const [action, setAction] = useState(null);
  const [tableColumns, setTableColumns] = useState([]);
  const [lastKey, setLastKey] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [currentPageSize, setCurrentPageSize] = useState(25);
  const [searchStr, setSearchStr] = useState("");

  const DateTimeDisplay = ({ timestamp, timezone, format = "MM/DD/YYYY" }) => {
    let date = convertDate(timestamp, format, false, timezone);
    let time = convertDate(timestamp, "HH:mm", false, timezone);
    return (
      <div className="d-flex align-items-center">
        <OverlayTrigger
          placement="right"
          delay={{ show: 150, hide: 250 }}
          overlay={<Tooltip style={{ zIndex: 9999999999 }}>{time}</Tooltip>}
        >
          <span>
            {date}
            <FaRegClock
              style={{
                marginLeft: "15px",
              }}
            ></FaRegClock>
          </span>

        </OverlayTrigger>
      </div>
    );
  };

  const handleClose = async (errorMsg) => {
    // show toast message if we received one from the modal
    if (errorMsg) {
      toast.error(errorMsg);
    }

    handleCancel();

    props.setPageLoading(true);
    await loadData(type, config);
    props.setPageLoading(false);
  };

  const handleCancel = () => {
    setAction(null);
  };

  useEffect(() => {
    let type = getType(props);
    setType(type);
    onLoad(type);
  }, [props.isAuthenticated, props.userObj, props.location]);

  // track previous search string
  const prevSearchStrRef = useRef();
  useEffect(() => {
    prevSearchStrRef.current = searchStr;
  });
  const prevSearchStr = prevSearchStrRef.current;

  useEffect(() => {
    // don't run on first execution
    if (prevSearchStr === undefined) {
      return;
    }
    // on page change, check if we are close to the end of the list and should load some more data
    let totalLoaded = displayObjects.length;
    let totalPages = totalLoaded / currentPageSize;
    let needMoreRecords = totalPages - currentPage < 4 && !!lastKey;

    let searchStrChanged = searchStr != prevSearchStr;

    // if search param changed then our lastKey is no longer relevant
    let lastKeyStr = lastKey;
    if (searchStrChanged) {
      setLastKey(null);
      lastKeyStr = null;
    }

    // only load more data if we are toward the end of what we have loaded or if the search string changed
    if (needMoreRecords || searchStrChanged) {
      loadData(type, config, lastKeyStr, searchStr);
    }
  }, [currentPage, currentPageSize, searchStr]);

  async function onLoad(type) {
    props.setPageLoading(true);
    try {
      let config = getConfig(type);
      setConfig(config);

      let records = await loadData(type, config);
      let planDetail = null;
      //get the ESPP details for the current period
      if (
        type === Constants.dbTypes.PERIOD ||
        type === Constants.dbTypes.USER
      ) {
        try {
          const ESPPDetails = await getList(
            Constants.dbTypes.PLAN_DETAILS,
            props.activeCompany
          );
          if (ESPPDetails && ESPPDetails.length > 0) {
            planDetail = {
              ...ESPPDetails[0],
            };
            setPlan(ESPPDetails[0]);
            setIsESPP(true);
          } else {
            setIsESPP(false);
          }
        } catch (e) {
          toast.error(e);
        }
      }
      populateColumns(config, type, planDetail, records);
    } catch (e) {
      toast.error(e);
    }
    props.setPageLoading(false);
  }
  async function loadData(type, config, lastKey, searchStr) {
    try {
      if (!type) {
        return;
      }
      let params = {
        lastKey: lastKey ? lastKey : null,
        searchStr: searchStr ? searchStr : null,
        paginate: type === "User" ? true : false,
      };
      let loadedRecords = await getList(type, props.activeCompany, params);
      console.log("props.activeCompany", props.activeCompany);
      if (loadedRecords.lastKey) {
        setLastKey(loadedRecords.lastKey);
      }
      if (type === Constants.dbTypes.PERIOD && loadedRecords.length > 0)
        setPeriods(true);
      else setPeriods(false);
      if (loadedRecords && loadedRecords.length > 0) {
        let i = 0;
        for (let d of loadedRecords) {
          d.actions = config.actions;
          d.index = i++;
          if (type === Constants.dbTypes.COMPANY && 'autopay' in d && d.autopay.length === 2) {
            d.autopayDay1 = d.autopay[0];
            d.autopayDay2 = d.autopay[1];
          }
        }
      }
      if (lastKey) {
        loadedRecords = displayObjects.concat(loadedRecords);
      }
      populateColumns(config, type, plan, loadedRecords);
      setDisplayObjects(loadedRecords);
      return loadedRecords;
    } catch (e) {
      toast.error(e);
    }
  }

  function populateColumns(config, type, plan = null, displayObjects = null) {
    let columns = [];
    // Push actions column
    if (config.actionEdit || config.actionDelete || config.actions) {
      columns.push({
        dataField: "actions",
        classes: "p-1",
        text: "Actions",
        headerClasses: (column, colIndex) =>
          colIndex === 0 && config.actions?.length < 2 ? "th-actions" : null,
        formatter: (cell, row, rowIndex, formatExtraData) => {
          if (cell?.length > 0) {
            return (
              <div className="d-flex align-items-center justify-content-center">
                {cell.map((a, index) => {
                  // check if this action has a condition that must be met to be enabled; if it is not enabled we will not show it on this row
                  let enabled = a.enabled ? a.enabled(row) : true;
                  // check if permission is specified for this action, if yes check if the action is allowed
                  let allowed = a.permission
                    ? a.permission === props.userObj.permission
                      ? true
                      : false
                    : true;

                  return enabled && allowed ? (
                    <a
                      key={index}
                      onClick={() => handleActionClick(a, row)}
                      className="mr2 cursor-pointer"
                      target={a.target}
                    >
                      <Icons
                        icon={a.icon instanceof Function ? a.icon(row) : a.icon}
                        className="edit"
                        aria-placeholder={a.title}
                      ></Icons>
                    </a>
                  ) : null;
                })}
              </div>
            );
          }
        },
      });
    }

    for (let key in config.fieldTypes) {
      let classes = "p-1";

      if (config.fieldTypes[key].classes) {
        classes = classes + " " + config.fieldTypes[key].classes;
      }
      columns.push({
        dataField: key,
        classes,
        text: config.fieldTypes[key].fieldName,
        headerClasses: config.fieldTypes[key].headerClasses,
        formatter: (cell, row, rowIndex, formatExtraData) => {
          if (
            cell != null &&
            config &&
            config.fieldTypes[formatExtraData].type !== "switch"
          ) {
            let fieldType = config.fieldTypes[formatExtraData].type;
            if (fieldType) {
              switch (fieldType) {
                case "date":
                  if (type === Constants.dbTypes.USER)
                    return formatDate(
                      cell,
                      config.fieldTypes[formatExtraData].dateformat
                    );
                  else
                    return (
                      <DateTimeDisplay
                        timestamp={cell}
                        timezone={props.activeCompany.timezone}
                      ></DateTimeDisplay>
                    );
                case "dropdown":
                  if (
                    type === Constants.dbTypes.USER &&
                    (config.fieldTypes[formatExtraData].fieldName ===
                      "Country" ||
                      config.fieldTypes[formatExtraData].fieldName === "State")
                  ) {
                    return cell;
                  }
                  if (
                    type === Constants.dbTypes.COMPANY &&
                    (config.fieldTypes[formatExtraData].fieldName ===
                      "Autopay Days")
                  ) {
                    return cell.join(',');
                  }
                  return getDropDownValue(formatExtraData, cell, config);
                case "currency":
                  if (
                    type === Constants.dbTypes.USER &&
                    config.fieldTypes[formatExtraData].fieldName ===
                    "Contribution Amount" &&
                    plan
                  ) {
                    return formatCurrency(
                      cell * (12 / Number(plan.purchasePeriod))
                    );
                  }
                  return formatCurrency(cell);
                case "link":
                  if (
                    !cell ||
                    props.userObj.permission !==
                    Constants.PermissionLevel.TAABL_ADMIN
                  ) {
                    return <></>;
                  }
                  let fileName = null;
                  if (cell && typeof cell !== "string" && cell.length > 0) {
                    fileName = cell[cell.length - 1].key;
                  } else {
                    fileName = cell;
                  }
                  return <ViewFileURL fileName={fileName} />;
                default:
                  return cell;
              }
            }
          }
          if (
            type === Constants.dbTypes.USER &&
            config.fieldTypes[formatExtraData].type === "percentage" &&
            plan
          ) {
            if (row.contributionAmount && row.salary) {
              return `${round(
                (row.contributionAmount / row.salary) *
                ((12 / Number(plan.purchasePeriod)) * 100)
              )}%`;
            } else return cell;
          }
          if (
            type === Constants.dbTypes.USER &&
            config.fieldTypes[formatExtraData].type === "switch"
          ) {
            return (
              <div>
                <Form.Group controlId="autopay" className={`form-group`}>
                  <Form.Check
                    id={`switchEnabled-${row.id}`}
                    type="switch"
                    defaultChecked={cell}
                    onChange={(e) => {
                      toggleAutoPay(
                        { ...row },
                        e.target.checked,
                        displayObjects
                      );
                    }}
                    label=""
                    disabled={!row.bank_information}
                  ></Form.Check>
                </Form.Group>
              </div>
            );
          }
          return "";
        },
        formatExtraData: key,
      });
    }

    setTableColumns(columns);
  }

  function getType(props) {
    if (props.location) {
      let type = props.location.pathname.split("/");
      type = type[type.length - 1];
      return type;
    }
    return props.type;
  }
  function showConfirm(obj) {
    confirmAlert({
      message: "Are you sure you want to delete?",
      buttons: [
        {
          label: "Yes",
          onClick: () => deleteObject(obj),
        },
        {
          label: "No",
          className: "btn-cancel",
        },
      ],
    });
  }
  const toggleAutoPay = async (userObj, enabled, displayObjects) => {
    let saveFields = {
      userId: userObj.id,
      autopay: enabled,
      activeCompanyId: userObj.companyId,
    };
    //update the userObj with salary and flags
    try {
      if ("autopay" in userObj) {
        await update(Constants.dbTypes.AUTOPAY, saveFields);
      } else {
        await create(Constants.dbTypes.AUTOPAY, saveFields);
      }
      if (displayObjects && displayObjects.length > 0) {
        for (let obj of displayObjects) {
          if (obj.id === userObj.id) {
            obj.autopay = enabled;
          }
        }
        setDisplayObjects(displayObjects);
      }
      toast.success(
        `Auto Transfer ${enabled ? "enabled" : "disabled"} for this User`
      );
    } catch (e) {
      if (e.response && e.response.data && e.response.data.error) {
        toast.error(e.response.data.error);
      } else {
        toast.error(e);
      }
    }
  };

  async function deleteObject(obj) {
    let objType = getType(props);
    let params = null;
    if (objType === Constants.dbTypes.PERIOD) {
      params = {
        periodStartdate: obj.periodStartdate,
        periodEnddate: obj.periodEnddate,
      };
    }
    try {
      await delObj(objType, obj.id, props.activeCompany.id, params);
    } catch (e) {
      if (e.response && e.response.data && e.response.data.error) {
        toast.error(e.response.data.error);
      } else {
        toast.error(e);
      }
    }
    onLoad();
  }
  const sizePerPageOptionRenderer = ({ text, page, onSizePerPageChange }) => (
    <a
      key={text}
      tabIndex="-1"
      role="menuitem"
      data-page={page}
      onMouseDown={(e) => {
        e.preventDefault();
        onSizePerPageChange(page);
      }}
    >
      <li role="presentation" className="dropdown-item">
        {text}
      </li>
    </a>
  );

  const contentTable = ({ paginationTableProps }) => (
    <div>
      <ToolkitProvider
        keyField="email"
        columns={tableColumns}
        data={displayObjects}
        search
      >
        {(toolkitprops) => (
          <div>
            {/* // TODO: make this look nice */}
            <DelayedInput
              minimum={2}
              delayms={800}
              onFinished={(val) => setSearchStr(val)}
            >
              <input
                id="search-bar-0"
                type="text"
                aria-labelledby="search-bar-0-label"
                className="form-control "
                placeholder="Search"
                onKeyUp={(e) => {
                  if (e.key === "Enter") {
                    setSearchStr(e.target.value);
                    e.target.blur();
                  }
                }}
              />
            </DelayedInput>

            <BootstrapTable
              wrapperClasses="table-fixed-height-wrap mb-2"
              classes="table-taabl"
              bordered={false}
              noDataIndication={() => <div>No data found</div>}
              {...toolkitprops.baseProps}
              {...paginationTableProps}
            />
          </div>
        )}
      </ToolkitProvider>
    </div>
  );

  function renderTable(displayObjects) {
    let pageSizeList = [
      {
        text: "25",
        value: 25,
      },
    ];
    if (displayObjects?.length) {
      let i = displayObjects?.length;
      if (i > 100) {
        i = 100;
      }
      while (i >= 25) {
        let value = pageSizeList[pageSizeList.length - 1].value + 25;
        pageSizeList.push({ text: "" + value, value });
        i -= 25;
      }
    }
    const options = {
      sizePerPage: currentPageSize,
      hidePageListOnlyOnePage: true,
      showTotal: false,
      sizePerPageList: pageSizeList,
      onPageChange: (newPage) => {
        setCurrentPage(newPage);
      },
      onSizePerPageChange: (newPageSize) => {
        setCurrentPageSize(newPageSize);
      },
      sizePerPageOptionRenderer,
    };
    if (props.pagination) {
      return (
        <PaginationProvider pagination={paginationFactory(options)}>
          {contentTable}
        </PaginationProvider>
      );
    } else {
      return (
        <BootstrapTable
          id="list"
          bootstrap4={true}
          wrapperClasses="table-vw-height mb-2"
          classes="table-taabl"
          keyField="index"
          bordered={false}
          data={displayObjects}
          columns={tableColumns}
          noDataIndication={() => <div>No data found</div>}
        />
      );
    }
  }

  function getDropDownValue(key, value, config) {
    if (config) {
      let d = config.fieldTypes[key].options.find((f) => f.key === value);
      if (d) {
        return d.value;
      }
    }
    return "";
  }

  function renderLinkCreateESPP() {
    toast.error("No ESPP Details available for this Company");
    return (
      <Button className="mb2" id="addnew" href={`/planDetails`}>
        Create a New ESPP
      </Button>
    );
  }
  async function addPeriodToOfferingPeriod() {
    if (
      type === Constants.dbTypes.PERIOD &&
      props.activeCompany.id === Constants.TaablAdminCompany
    ) {
      toast.error(
        "Not allowed to add Period to Offering Period for TaablAdmin Company"
      );
    } else {
      props.setPageLoading(true);
      try {
        if (!("enrollmentStarts" in plan) || !("enrollmentEnds" in plan)) {
          toast.error(
            "Please set Enrollment starts  & Enrollment ends in Plan Details page."
          );
        } else {
          let record = buildNextPurchasePeriod(
            displayObjects[displayObjects.length - 1],
            props.activeCompany,
            plan
          );
          record.type = Constants.dbTypes.PERIOD;
          await create(Constants.dbTypes.PERIOD, record);
          toast.success("Period added successfully");
        }
      } catch (e) {
        if (e.response && e.response.data && e.response.data.error) {
          toast.error(e.response.data.error);
        } else {
          toast.error(e);
        }
      }
      await loadData(type, config);
      props.setPageLoading(false);
    }
  }
  function handleActionClick(action, obj) {
    action = Object.assign(action, { obj: obj });
    setAction(action);
    switch (action.action_type) {
      case "component":
        break;
      case "open_people_sheet":
        setOpenSideSheet(obj);
        setTab("dashboard");
        break;
      case "edit":
        let editObj = {
          type: getType(props),
          id: obj.id,
        };
        props.history.push(`/editObj/${JSON.stringify(editObj)}`);
        break;
      case "delete":
        showConfirm(obj);
        break;
      case "switch":
        let selectedCompany = { ...obj };
        delete selectedCompany.actions;
        localStorage.setItem("taabl-ac", JSON.stringify(selectedCompany));
        props.setActiveCompany(selectedCompany);
        props.history.push("/");
        break;
      default:
        break;
    }
  }
  // this function close the sidesheet
  function onClose() {
    setOpenSideSheet(null);
  }

  // This function is used to set password
  async function resetPassword() {
    if (openSideSheet) {
      try {
        await update(
          Constants.dbTypes.USER,
          {
            resetPassword: true,
            id: openSideSheet.id,
          },
          props.activeCompany
        );
        toast.success("We have sent password reset email to the user");
      } catch (err) {
        toast.error(
          "Unable to send reset password email. Please contact taabl admin"
        );
      }
    }
  }

  //needed to go from one screen to the other.
  let load = props.pageLoading || getType(props) !== type;
  return (
    <PageLoader isActive={load} individual={false}>
      <div className="p5">
        {!props.hideTitle && (
          <h2 className="text-capitalize mb-2">
            {config && config.displayName ? config.displayName : type}
          </h2>
        )}
        {!load && !props.disAllowNewUser && type !== Constants.dbTypes.PERIOD && (
          <Button
            className="mb2"
            id="addnew"
            href={`/editObj/${JSON.stringify({ type: type })}`}
          >
            {"\uFF0B"} Create New {type}
          </Button>
        )}
        {!load &&
          !props.disAllowNewUser &&
          type === Constants.dbTypes.PERIOD &&
          isESPP &&
          !periods && (
            <Button
              className="mb1"
              size="sm"
              id="addnew"
              href={`/editObj/${JSON.stringify({
                type: Constants.dbTypes.OFFERING_PERIOD,
              })}`}
            >
              {"\uFF0B"} Create Offering Period
            </Button>
          )}
        {!load &&
          !props.disAllowNewUser &&
          type === Constants.dbTypes.PERIOD &&
          isESPP &&
          periods && (
            <Button
              className="mb1"
              size="sm"
              id="addnew"
              onClick={addPeriodToOfferingPeriod}
            >
              {"\uFF0B"} Add Period
            </Button>
          )}

        {(type !== Constants.dbTypes.PERIOD ||
          (type === Constants.dbTypes.PERIOD && isESPP)) &&
          !load &&
          config &&
          renderTable(displayObjects)}
        {!load &&
          type === Constants.dbTypes.PERIOD &&
          !isESPP &&
          renderLinkCreateESPP()}

        {openSideSheet && (
          <SideSheet onClose={onClose}>
            <a onClick={resetPassword} className="resetbtn">
              Reset Password
            </a>
            <div className="pt-4 ps-4 pe-4 w-100">
              <span className="fw-bold dashboard-user">
                {openSideSheet?.firstName} {openSideSheet?.lastName}
              </span>
              <div className="dashboard-emid">{openSideSheet?.empid}</div>
              {permissionValue.map((item, index) => {
                const newPermissionVal =
                  item.key === openSideSheet?.permission ? item.value : "";
                return (
                  <div key={index} className="dashboard-emid">
                    {newPermissionVal}
                  </div>
                );
              })}
              <div className=" inside">
                <Tabs
                  id="sidesheet-tabs"
                  activeKey={tab}
                  onSelect={(k) => setTab(k)}
                  variant="taabl"
                >
                  <Tab eventKey="dashboard" title="Dashboard">
                    <div className="me-3 mt-2 dashobard-b tabs">
                      <EmployeeHome
                        activeCompany={props.activeCompany}
                        user={openSideSheet}
                      ></EmployeeHome>
                    </div>
                  </Tab>
                  <Tab eventKey="contribution" title="Contribution">
                    <div className="me-3 mt-2 dashobard-b tabs">
                      <Contributions
                        activeCompany={props.activeCompany}
                        userObj={openSideSheet}
                      ></Contributions>
                    </div>
                  </Tab>
                </Tabs>
              </div>
            </div>
          </SideSheet>
        )}
        <Modal
          show={action && action.action_type === "modal"}
          onHide={handleClose}
          centered
          backdrop="static"
        >
          {action &&
            action.modal_component &&
            React.createElement(action.modal_component, {
              onHide: handleClose,
              onCancel: handleCancel,
              object: action.obj,
              activeCompany: props.activeCompany,
            })}
        </Modal>
      </div>
    </PageLoader>
  );
}
